import { Button, Popover, PopoverContent, PopoverTrigger } from '@e2n/ui/v2';
import { cn } from '@/lib/utils';
import {
  faAnglesUpDown,
  faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* eslint-disable max-lines-per-function */
export function Select<T extends { id: string; name?: string }>({
  id,
  invalid = false,
  onChange,
  options,
  selectedOption,
  drawerLabel,
  renderListItem,
  renderTriggerContent,
  disabled = false,
  getOptionLabel = (option) => option.name ?? '',
  searchAddon,
  emptyText,
}: {
  id?: string;
  invalid?: boolean;
  onChange: (option: T) => void;
  drawerLabel: string;
  selectedOption?: T | undefined;
  options: T[];
  renderListItem: (option: T, selectedOption?: T) => ReactNode;
  renderTriggerContent?: (selectedOption: T) => ReactNode;
  disabled?: boolean;
  /** Text für Suche und Default-Trigger – nötig, wenn `T` kein `name` hat. */
  getOptionLabel?: (option: T) => string;
  /** Zusätzliches Element rechts neben dem Suchfeld (z. B. ein Filter). */
  searchAddon?: ReactNode;
  /** Text bei leerer Trefferliste – Default: „Keine Ergebnisse". */
  emptyText?: string;
}) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option)
      .toLocaleLowerCase()
      .includes(filter.toLocaleLowerCase()),
  );

  // Jeder Schließweg läuft hierüber, damit der Suchtext nie in die nächste
  // Öffnung übernommen wird.
  function handleOpenChange(open: boolean) {
    setIsOpen(open);

    if (!open) {
      setFilter('');
    }
  }

  return (
    // `modal`: Innerhalb eines Sheets blockiert dessen Scroll-Lock sonst das
    // Mausrad in der (per Portal außerhalb gerenderten) Liste.
    <Popover modal open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Trigger
          id={id}
          invalid={invalid}
          disabled={disabled}
          selectedOption={selectedOption}
          renderTriggerContent={renderTriggerContent}
          getOptionLabel={getOptionLabel}
        />
      </PopoverTrigger>
      {/* Der V2-Popover wird unterhalb von `md` selbst zum Drawer – deshalb
          nur noch ein Trigger und ein Inhalt statt zweier Varianten. */}
      <PopoverContent
        align="start"
        className="gap-0 max-md:gap-4 max-md:pt-3 md:w-(--radix-popover-trigger-width) md:min-w-33">
        <div className="px-4 text-center text-sm font-semibold text-labels-primary md:hidden">
          {drawerLabel}
        </div>
        {/* Suchfeld, Trenner und Zeilenmaße spiegeln `ComboboxSearch`/
            `ComboboxList`/`ComboboxItem` aus dem V2-Kit – die Bausteine
            selbst brauchen den Combobox-Context und sind hier nicht nutzbar. */}
        <div className="flex shrink-0 items-center gap-2 px-4 pt-4 pb-1 max-md:pt-0 max-md:pb-2">
          <div className="flex h-9 min-w-0 flex-1 items-center gap-1 rounded-full border-2 border-neutrals-v2-300 px-4 focus-within:border-brand-blue-100">
            <FontAwesomeIcon
              className="size-5.5 shrink-0 text-brand-red-200"
              icon={faMagnifyingGlass}
            />
            <input
              className="min-w-0 flex-1 bg-transparent text-sm leading-5 text-labels-primary outline-none placeholder:text-labels-secondary"
              value={filter}
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          {searchAddon}
        </div>
        <div className="max-h-72 overflow-y-auto pb-1 max-md:mx-4 md:max-h-[min(20rem,var(--radix-popover-content-available-height))]">
          {filteredOptions.length < 1 ? (
            <div className="px-4 py-1.5 text-sm leading-5 text-labels-secondary">
              {emptyText ??
                t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}
            </div>
          ) : (
            <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-separators-non-opaque">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="flex w-full cursor-pointer items-center rounded-none px-4 py-1.5 text-left text-sm leading-5 text-labels-primary outline-none select-none hover:bg-neutrals-v2-200 focus-visible:bg-neutrals-v2-200"
                  onClick={() => {
                    onChange(option);
                    handleOpenChange(false);
                  }}>
                  <span className="flex h-7 min-w-0 flex-1 items-center">
                    {renderListItem(option, selectedOption)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Trigger<T extends { id: string; name?: string }>({
  selectedOption,
  renderTriggerContent,
  getOptionLabel,
  disabled,
  invalid,
  ...props
}: {
  selectedOption?: T;
  renderTriggerContent?: (selectedOption: T) => ReactNode;
  getOptionLabel: (option: T) => string;
  disabled: boolean;
  invalid: boolean;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();
  return (
    <Button
      {...props}
      aria-invalid={invalid}
      disabled={disabled}
      variant="outline"
      type="button"
      role="combobox"
      className={cn(
        // Feld-Optik des V2-Kits (`SelectTrigger`/`ComboboxTrigger`): weiße
        // Fläche, 2px-Rahmen, der sich bei offener Liste bzw. Fehler einfärbt.
        'w-full justify-between gap-1 border-2 border-neutrals-v2-200 bg-backgrounds-primary pr-3',
        'hover:bg-backgrounds-primary disabled:bg-backgrounds-primary disabled:text-labels-tertiary',
        'aria-invalid:border-error-red-200 data-[state=open]:border-brand-blue-100 font-normal',
        selectedOption ? 'text-labels-primary' : 'text-labels-secondary',
        props.className,
      )}>
      {renderTriggerContent && selectedOption
        ? renderTriggerContent(selectedOption)
        : selectedOption
          ? getOptionLabel(selectedOption)
          : t('actions.auswaehlen')}
      <FontAwesomeIcon className="size-4" icon={faAnglesUpDown} />
    </Button>
  );
}
